import MarkdownIt from 'markdown-it';
import markdownItSub from 'markdown-it-sub';
import markdownItSup from 'markdown-it-sup';
import { FilterXSS } from 'xss';

/**
 * Shared, read-only rich-text renderer for the account SPA.
 *
 * The `text`/`description` fields hold Markdown (Nova, the ЛК editor) or raw
 * HTML (pasted into either one), so both have to render. Markdown-it runs with
 * `html: true` to let embedded tags through, and the sanitizer below is what
 * makes the result safe for `v-html` — it is load-bearing, not defense in
 * depth.
 *
 * Content that already starts as an HTML block skips Markdown-it entirely:
 * pretty-printed markup would otherwise be mangled (four-space indentation
 * turns into code blocks, a leading `#` into a heading).
 *
 * Rendering to opaque HTML — instead of mounting md-editor's <MdPreview>, which
 * mutates its own DOM imperatively — is what keeps the renderer from fighting
 * Vue's virtual DOM during route teardown (DEV-1442).
 */

const md = new MarkdownIt({
  html: true,
  linkify: true,
  breaks: false,
})
  .use(markdownItSub)
  .use(markdownItSup);

const sanitizer = new FilterXSS({
  stripIgnoreTag: true,
  stripIgnoreTagBody: ['script', 'style'],
});

const HTML_BLOCK_START = /^\s*<(?:p|div|h[1-6]|ul|ol|table|blockquote|pre|section|article|figure|header|footer|main|img|br|hr)\b/i;

export function renderRichText(content: string | null | undefined): string {
  const source = (content ?? '').trim();

  if (source === '') {
    return '';
  }

  const html = HTML_BLOCK_START.test(source) ? source : md.render(source);

  return sanitizer.process(html);
}
